// components/AdminDashboardLockTable.js
//
// Admin-only fleet awareness surface (Option C). Compact table of all
// active locks across the fleet, sortable, with an "attention only" filter.
// Row click → navigates to the property's Locks tab. Investigation happens
// there; this widget is purely awareness.

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import lockApi from '../services/lockApi';
import useLanguage from '../hooks/useLanguage';
import { batteryColor } from '../utils/lockUiHelpers';
import { formatAgeLabel, hoursSince } from '../utils/alertDisplay';

// ─── Helpers ──────────────────────────────────────────────────────────────

const lockSortValue = (lock) => {
  // Default sort: attention-needed first, then battery ascending.
  // Lower number = higher in the list.
  const attentionRank = (lock.reasons?.length || 0) > 0 ? 0 : 1;
  const battery = typeof lock.battery?.electricQuantity === 'number'
    ? lock.battery.electricQuantity
    : 999;
  return [attentionRank, battery];
};

const compareLocks = (a, b) => {
  const av = lockSortValue(a);
  const bv = lockSortValue(b);
  if (av[0] !== bv[0]) return av[0] - bv[0];
  return av[1] - bv[1];
};

// ─── Component ────────────────────────────────────────────────────────────

const AdminDashboardLockTable = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attentionOnly, setAttentionOnly] = useState(false);
  // Collapsed by default so the dashboard leads with the day's ACTION surface
  // (arrival readiness) rather than this passive fleet view. It auto-opens
  // below whenever a lock actually needs attention — collapsing must hide
  // noise, never a problem.
  const [expanded, setExpanded] = useState(false);

  const fetchLocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await lockApi.getActiveLocks();
      setLocks(response.locks || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocks(); }, [fetchLocks]);

  const filtered = useMemo(() => {
    let list = locks;
    if (attentionOnly) {
      list = list.filter((l) => (l.reasons?.length || 0) > 0);
    }
    return [...list].sort(compareLocks);
  }, [locks, attentionOnly]);

  const attentionCount = useMemo(
    () => locks.filter((l) => (l.reasons?.length || 0) > 0).length,
    [locks]
  );

  // Attention DEFAULTS the section open — a collapsed card must never be the
  // reason nobody saw a flat battery — but the operator can still close it
  // once seen. A toggle that silently refuses to move reads as broken.
  const [userToggled, setUserToggled] = useState(false);
  const isOpen = userToggled ? expanded : (expanded || attentionCount > 0);
  const toggle = () => { setUserToggled(true); setExpanded(!isOpen); };

  const handleRowClick = (lock) => {
    const propertyId = lock.property?._id || lock.property;
    if (!propertyId) return;
    navigate(`/properties/${propertyId}?tab=locks`);
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="card-body text-center p-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">{t('locks.panel.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="card-body">
          <h5 className="card-title">{t('locks.table.heading')}</h5>
          <div className="alert alert-danger mb-0 d-flex justify-content-between align-items-center">
            <span><strong>{t('locks.table.error.title')}:</strong> {error}</span>
            <button className="btn btn-sm btn-outline-danger" onClick={fetchLocks}>
              {t('locks.table.error.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (locks.length === 0) {
    return (
      <div className="card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="card-body">
          <h5 className="card-title">{t('locks.table.heading')}</h5>
          <p className="text-muted mb-0">{t('locks.table.noLocks')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-2">
          <div
            role="button"
            tabIndex={0}
            onClick={toggle}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
            style={{ cursor: 'pointer' }}
          >
            <h5 className="card-title mb-0">
              <i className={`bi bi-chevron-${isOpen ? 'down' : 'left'} me-2`} style={{ fontSize: '14px' }}></i>
              <i className="bi bi-key me-2"></i>
              {t('locks.table.heading')}
              {/* The count travels with the collapsed header, so a folded card
                  still says whether anything is wrong. */}
              {attentionCount > 0 ? (
                <span className="badge bg-warning text-dark ms-2">{attentionCount}</span>
              ) : (
                <span className="badge bg-light text-muted ms-2">{locks.length}</span>
              )}
            </h5>
            <small className="text-muted">{t('locks.table.subtitle')}</small>
          </div>
          {isOpen && (
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="lockAttentionOnly"
                checked={attentionOnly}
                onChange={(e) => setAttentionOnly(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="lockAttentionOnly">
                {t('locks.table.attentionFilter')}
              </label>
            </div>
          )}
        </div>

        <div className="table-responsive" style={{ display: isOpen ? undefined : 'none' }}>
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>{t('locks.table.column.lock')}</th>
                <th>{t('locks.table.column.property')}</th>
                <th>{t('locks.table.column.battery')}</th>
                <th>{t('locks.table.column.online')}</th>
                <th>{t('locks.table.column.alerts')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lock) => {
                const reasons = lock.reasons || [];
                const battery = lock.battery?.electricQuantity;
                return (
                  <tr
                    key={lock.lockMongoId}
                    onClick={() => handleRowClick(lock)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>{lock.lockAlias || '—'}</div>
                      <small className="text-muted">ID: {lock.lockId}</small>
                    </td>
                    <td>
                      {lock.property?.name || '—'}
                      {lock.property?.propertyCode && (
                        <small className="text-muted d-block">{lock.property.propertyCode}</small>
                      )}
                    </td>
                    <td>
                      <span style={{ color: batteryColor(battery), fontWeight: 500 }}>
                        <i className="bi bi-battery-half me-1"></i>
                        {battery != null ? `${battery}%` : '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: lock.isOnline ? '#28a745' : '#6c757d' }}>
                        <i className={`bi bi-${lock.isOnline ? 'wifi' : 'wifi-off'} me-1`}></i>
                        {lock.isOnline ? t('locks.panel.online') : t('locks.panel.offline')}
                      </span>
                      {/* Duration is the point: without it a 10-minute blip and a
                          five-day outage look identical, which is how a whole
                          building stayed broken unnoticed (prod 2026-07-28). */}
                      {!lock.isOnline && lock.unreachableSince && (
                        <small className="text-muted d-block">
                          {formatAgeLabel(hoursSince(lock.unreachableSince))}
                        </small>
                      )}
                    </td>
                    <td>
                      {reasons.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        reasons.map((r, idx) => (
                          <span key={idx} className="badge bg-warning text-dark me-1 mb-1">
                            {t(`locks.table.reasons.${r.type}`)}
                            {r.value !== undefined && ` (${r.value}%)`}
                          </span>
                        ))
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardLockTable;
